<template>
  <v-expansion-panel v-if="selectedToken === ''" class="PlanProperties pa-0">
    <v-expansion-panel-header>{{ is3d ? "Simulation" : "Cell Properties" }}</v-expansion-panel-header>
    <v-expansion-panel-content class="pa-0">
      <v-col cols="12" class="pa-0">
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model="cellTitle"
              outlined
              placeholder="1"
              label="Title"
              hint="To find this simulation later"
              color="primary"
              type="text"
              dense
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="cellDescription"
              outlined
              placeholder="1"
              label="Description"
              hint="To understand this simulation"
              color="primary"
              dense
              type="text"
            />
          </v-col>
          <!-- Célula, tempo e bordas só existem no 2D (Meep); o domínio 3D é automático -->
          <template v-if="!is3d">
          <v-col cols="6">
            <v-text-field
              v-model="cellResolution"
              outlined
              dense
              placeholder="1"
              label="Resolution (pixels/µm)"
              hint="≥ 10·n/λ (10 pixels per wavelength inside the densest material)"
              color="primary"
              type="number"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="cellUntil"
              outlined
              placeholder="1"
              label="Time (µm/c)"
              hint="Duration of the field picture / video"
              dense
              color="primary"
              type="number"
            />
          </v-col>
          <v-row dense>
            <v-col cols="4">
              <v-text-field
                v-model="coordinatesX"
                outlined
                placeholder="1"
                dense
                label="Width X (µm)"
                hint="Cell size along x, including the PML"
                color="primary"
                type="number"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="coordinatesY"
                outlined
                placeholder="1"
                dense
                label="Height Y (µm)"
                hint="Cell size along y, including the PML"
                color="primary"
                type="number"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="coordinatesZ"
                outlined
                placeholder="0"
                disabled
                dense
                label="Z (µm)"
                hint="Not used in 2D"
                color="primary"
                type="number"
              />
            </v-col>
          </v-row>
          <v-col cols="12">
            <v-text-field
              v-model="cellPMLThickness"
              outlined
              placeholder="1"
              label="PML thickness (µm)"
              hint="Absorbing border, ≥ λ/2"
              dense
              color="primary"
              type="number"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="cellBoundary"
              :items="boundaryOptions"
              outlined
              dense
              label="Borders"
              :hint="boundaryHint"
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="cellDecayThreshold"
              :items="precisionOptions"
              outlined
              dense
              label="Spectra accuracy"
              hint="When to stop the spectral runs. Use “High” for sharp resonances (slower)"
              persistent-hint
            />
          </v-col>
          </template>
          <v-col v-else cols="12">
            <v-select
              v-model="meshQuality"
              :items="qualityOptions"
              outlined
              dense
              label="Accuracy (mesh and solver)"
              :hint="qualityHint"
              persistent-hint
            />
            <p class="text-caption mt-3 mb-0">
              The 3D solver (finite elements) computes the field of the sources at a single
              wavelength around your geometries. The air box around them is created
              automatically.
            </p>
          </v-col>
        </v-row>
        <v-btn block color="primary" @click="openPlotOptions(true)">
          Run Simulation 
        </v-btn>
      </v-col>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
const QUALITY_HINTS = {
  fast: "Quick preview (seconds to a minute). Qualitative: errors of 30–60% in the field",
  accurate: "Recommended. Second-order elements: errors of about 5–20%, usually under 2 minutes",
  reference: "Absorbing PML layer and finer mesh: errors of 3–10%, but 5–20× slower. Only for small scenes (≲ 2 wavelengths)"
};

const BOUNDARY_HINTS = {
  pml: "Isolated structures, waveguides and cavities",
  pml_x_periodic_y:
    "Plane wave travelling along x through interfaces, slabs or gratings. Source and outputs must span the full height",
  pml_y_periodic_x:
    "Plane wave travelling along y. Source and outputs must span the full width"
};

export default {
  name: "CellProperties",
  data: () => ({
    boundaryOptions: [
      { value: "pml", text: "Absorbing (PML) on all sides" },
      { value: "pml_x_periodic_y", text: "PML in x, periodic in y" },
      { value: "pml_y_periodic_x", text: "PML in y, periodic in x" }
    ],
    qualityOptions: [
      { value: "fast", text: "Fast (preview)" },
      { value: "accurate", text: "Accurate (recommended)" },
      { value: "reference", text: "Reference (slow, small scenes)" }
    ],
    precisionOptions: [
      { value: 1e-3, text: "Normal (fields decay to 10⁻³)" },
      { value: 1e-5, text: "High (fields decay to 10⁻⁵)" }
    ]
  }),
  computed: {
    ...mapGetters("simulator", [
      "selectedToken",
      "coordinates",
      "title",
      "description",
      "resolution",
      "until",
      "pmlThickness",
      "boundary",
      "decayThreshold",
      "quality3d",
      "cell"
    ]),
    is3d() {
      return this.$store.state.simulator.is3d;
    },
    qualityHint() {
      return QUALITY_HINTS[this.quality3d];
    },
    meshQuality: {
      get() {
        return this.quality3d;
      },
      set(value) {
        this.setSimulationOption(["quality3d", value]);
      }
    },
    boundaryHint() {
      return BOUNDARY_HINTS[this.boundary];
    },
    cellBoundary: {
      get() {
        return this.boundary;
      },
      set(value) {
        this.setSimulationOption(["boundary", value]);
      }
    },
    cellDecayThreshold: {
      get() {
        return this.decayThreshold;
      },
      set(value) {
        this.setSimulationOption(["decayThreshold", Number(value)]);
      }
    },
    cellTitle: {
      get() {
        return this.title;
      },
      set(value) {
        this.setTitle(value);
      }
    },
    cellDescription: {
      get() {
        return this.description;
      },
      set(value) {
        this.setDescription(value);
      }
    },
    cellUntil: {
      get() {
        return this.until;
      },
      set(value) {
        this.setUntil(value);
      }
    },
    cellResolution: {
      get() {
        return this.resolution;
      },
      set(value) {
        this.setResolution(value);
      }
    },
    cellPMLThickness: {
      get() {
        return this.pmlThickness;
      },
      set(value) {
        this.setPMLThickness(Number(value));
      }
    },
    coordinatesX: {
      get() {
        return Number(this.coordinates.x);
      },
      set(value) {
        this.setCoordinates({
          y: Number(this.coordinates.y),
          z: Number(this.coordinates.z),
          x: Number(value)
        });
      }
    },
    coordinatesY: {
      get() {
        return Number(this.coordinates.y);
      },
      set(value) {
        this.setCoordinates({
          x: Number(this.coordinates.x),
          z: Number(this.coordinates.z),
          y: Number(value)
        });
      }
    },
    coordinatesZ: {
      get() {
        return Number(this.coordinates.z);
      },
      set(value) {
        this.setCoordinates({
          x: Number(this.coordinates.x),
          y: Number(this.coordinates.y),
          z: Number(value)
        });
      }
    }
  },
  methods: {
    ...mapActions("simulator", [
      "setPMLThickness",
      "setSimulationOption",
      "setTitle",
      "setDescription",
      "setResolution",
      "setUntil",
      "setCoordinates",
      "openPlotOptions"
    ])
  }
};
</script>
